/**
 * Helpers for DocumentReminders factory methods
 */

import type { IBaseNotary } from "../../BaseNotaryFactory";
import type { DocumentRemindersConfig } from "le-coffre-resources/dist/Notary";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { buildEmptyPayload, buildReminderConfigPayload } from "@Front/Api/LeCoffreApi/helpers/businessRequestPayloadHelpers";

export interface UpdateConfigParams {
	reminderIntervals?: number[];
	reminderMessage?: string;
	autoSend?: boolean;
	isActive?: boolean;
}

/**
 * Create create config method
 */
export function createCreateConfigMethod(baseNotary: IBaseNotary) {
	return async (
		documentTypeUid: string,
		reminderIntervals: number[],
		reminderMessage?: string,
		autoSend = true,
	): Promise<DocumentRemindersConfig> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/document-types/${documentTypeUid}/reminders/config`));
		try {
			const payload = buildReminderConfigPayload(reminderIntervals, reminderMessage, autoSend);
			return await baseNotary.postRequest<DocumentRemindersConfig>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get config method
 */
export function createGetConfigMethod(baseNotary: IBaseNotary) {
	return async (documentTypeUid: string): Promise<DocumentRemindersConfig> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/document-types/${documentTypeUid}/reminders/config`));
		try {
			return await baseNotary.getRequest<DocumentRemindersConfig>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create update config method
 */
export function createUpdateConfigMethod(baseNotary: IBaseNotary) {
	return async (documentTypeUid: string, params: UpdateConfigParams): Promise<DocumentRemindersConfig> => {
		const { reminderIntervals, reminderMessage, autoSend, isActive } = params;
		const url = new URL(baseNotary.namespaceUrl.concat(`/document-types/${documentTypeUid}/reminders/config`));
		try {
			const payload = buildReminderConfigPayload(reminderIntervals, reminderMessage, autoSend, isActive);
			return await baseNotary.putRequest<DocumentRemindersConfig>(url, payload);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create delete config method
 */
export function createDeleteConfigMethod(baseNotary: IBaseNotary) {
	return async (documentTypeUid: string): Promise<{ message: string }> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/document-types/${documentTypeUid}/reminders/config`));
		try {
			return await baseNotary.deleteRequest<{ message: string }>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create get office configs method
 */
export function createGetOfficeConfigsMethod(baseNotary: IBaseNotary) {
	return async (officeUid: string): Promise<DocumentRemindersConfig[]> => {
		const url = new URL(baseNotary.namespaceUrl.concat(`/offices/${officeUid}/reminders/config`));
		try {
			return await baseNotary.getRequest<DocumentRemindersConfig[]>(url);
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create trigger reminders method
 */
export function createTriggerRemindersMethod(baseNotary: IBaseNotary) {
	return async (): Promise<{ message: string; count: number }> => {
		const url = new URL(baseNotary.namespaceUrl.concat("/reminders/send"));
		try {
			return await baseNotary.postRequest<{ message: string; count: number }>(url, buildEmptyPayload());
		} catch (err) {
			baseNotary.onError(err);
			return Promise.reject(err);
		}
	};
}

/**
 * Create count method (stub)
 */
export function createCountMethod(): () => Promise<{ count: number }> {
	return async (_query?: unknown): Promise<{ count: number }> => {
		LoggerService.warn("⚠️  DocumentRemindersApi.count() : Stub non implémenté");
		return { count: 0 };
	};
}

/**
 * Create get method (stub)
 */
export function createGetMethod(): () => Promise<DocumentRemindersConfig[]> {
	return async (_query?: unknown): Promise<DocumentRemindersConfig[]> => {
		LoggerService.warn("⚠️  DocumentRemindersApi.get() : Stub non implémenté");
		return [];
	};
}
